import {Tool} from "./tools";
import {MousePosition, ScaleLine, Zoom} from "ol/control";
import {createStringXY} from "ol/coordinate";
import {WGS84_CODE} from "../../common/config/config";
import '/modules/map/css/tools/location-history-tool.css';
import '/modules/map/css/tools/location-history-tool.responsive.css';
import '/modules/map/css/tools/location-history-tool.mobile.css';
import '/modules/map/css/tools/coordinates-tool.css';
import '/modules/map/css/tools/coordinates-tool.responsive.css';
import '/modules/map/css/tools/zoom-tool.css';
import '/modules/map/css/tools/zoom-tool.mobile.css';

/**
 * Extend the OpenLayers MousePosition control to also implement a view center position
 * @extends MousePosition
 */
class MapPosition extends MousePosition {

    /**
     *
     * @param opt_options OL options
     * @param controlsDOMElement {jQuery} The map controls DOM element
     * @param overlayDOMElement {jQuery} The map overlay DOM element
     */
    constructor(opt_options, controlsDOMElement, overlayDOMElement) {
        super(opt_options);
        this.controlsDOMElement = controlsDOMElement;
        this.crosshair = $(
            '<div class="crosshair-container">' +
            '  <div>' +
            '    <div class="vertical-line"></div>' +
            '    <div class="horizontal-line"></div>' +
            '  </div>' +
            '</div>'
        );
        overlayDOMElement.append(this.crosshair);
        if (tumbo.isMobileDevice) {
            this.showCrosshair();
        } else {
            this.hideCrosshair();
        }
    }

    getClassName(){
        return "MapPosition";
    }

    /**
     * Show the crosshair in the center of the view
     */
    showCrosshair() {
        this.isCrosshairVisible = true;
        this.crosshair.show();
    }

    /**
     * Hide the crosshair
     */
    hideCrosshair() {
        this.isCrosshairVisible = false;
        this.crosshair.hide();
    }

    /**
     * Is the crosshair visible?
     * @return {boolean}
     */
    isCrosshair() {
        return this.isCrosshairVisible;
    }

    handleMouseMove(event) {
        $('.ol-mouse-position',$(this.element).parent()).hide();
        $(this.element).show();
        if (!this.isCrosshair()) {
            super.handleMouseMove(event);
        }
    }

    handleMouseOut(event) {
        if (!this.isCrosshair()) {
            super.handleMouseOut(event);
        }
    }

    /**
     * Set the visible coordinates from the center of the view
     */
    updateCoordinatesFromCenter() {
        const coordinateFormat = this.getCoordinateFormat();
        const html = this.center === undefined
            ? ''
            : (coordinateFormat ? coordinateFormat(this.center) : this.center.toString());
        if (!this.renderedHTML_ || html !== this.renderedHTML_) {
            this.element.innerHTML = html;
            this.renderedHTML_ = html;
        }
    }

    /**
     * To be called when there is navigation on the map
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     */
    onNavigation(zoomLevel, centerWgs84) {
        this.zoomLevel = zoomLevel;
        this.center = centerWgs84;
        if (this.isCrosshair()) {
            this.updateCoordinatesFromCenter();
        }
    }
}

/**
 * The coordinates tool
 * @extends Tool
 */
class CoordinatesTool extends Tool {

    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param projection {Projection} The map projection
     * @param controlsDOMElement {jQuery} The controls DOM element
     * @param overlayDOMElement {jQuery} The map overlay DOM element
     * @param runtimeConfig {MapRuntimeConfig} The map runtime config
     */
    constructor(config, onChangeRuntimeConfig, projection, controlsDOMElement, overlayDOMElement, runtimeConfig) {
        super(config, runtimeConfig, onChangeRuntimeConfig);
        this.projection = projection;
        this.controlsDOMElement = controlsDOMElement;
        this.runtimeConfig = runtimeConfig?runtimeConfig:tumbo.runtimeConfig;

        this.mapPosition = new MapPosition({
            coordinateFormat: this.formatFunction(),
            projection: WGS84_CODE,
            target: $('.position-container', this.getUIElement())[0]
        }, this.controlsDOMElement, overlayDOMElement);
    }

    getClassName(){
        return "CoordinatesTool";
    }

    /**
     * Returns a function to format coordinates
     * @return {function(Array<number>): string}
     */
    formatFunction() {
        const stringify = createStringXY(4);
        return function (coords) {
            return "lon, lat: " + stringify(coords);
        }
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div class="coordinates ol-unselectable ol-control"></div>');

            let projectionContainer = $('<div class="projection-container"><div>' + this.projection.getName() + '</div></div>');
            this.uiElement.append(projectionContainer);

            let positionContainer = $('<div class="position-container"></div>');
            this.uiElement.append(positionContainer);
        }
        return this.uiElement;
    }

    getOLControls() {
        let uiElement = this.getUIElement();
        if (!this.olControls) {
            this.olControls = [
                new ScaleLine({
                    units: 'metric',
                    target: uiElement[0]
                }),
                this.mapPosition
            ];
        }
        return this.olControls;
    }

    onNavigationImmediately(zoomLevel, centerWgs84) {
        if (this.mapPosition) {
            this.mapPosition.onNavigation(zoomLevel, centerWgs84);
        }
    }

    /**
     * Show the crosshair in the center of the view
     */
    showCrosshair() {
        this.mapPosition.showCrosshair();
        this.mapPosition.updateCoordinatesFromCenter();
    }

    /**
     * Hide the crosshair
     */
    hideCrosshair() {
        this.mapPosition.hideCrosshair();
    }

    /**
     * Is the crosshair visible?
     */
    isCrosshair() {
        return this.mapPosition.isCrosshair();
    }

}


let ignoreLocationHistoryChange = false;
let historyIndex = -1;
let history = [];
let onNavCount = 0;

/**
 * A tool to navigate the user's navigation history
 * @extends Tool
 */
class LocationHistoryTool extends Tool {
    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param projection {Projection} The map projection
     * @param fit {function} A function that takes an extent (wgs84) and fits the map there
     */
    constructor(config, onChangeRuntimeConfig, projection, fit) {
        super(config, onChangeRuntimeConfig);
        this.projection = projection;
        this.fit = fit;
    }

    getClassName(){
        return "LocationHistoryTool";
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div id="location_history" class="ol-unselectable ol-control"></div>');

            const previousButton = $('<button type="button" class="history-previous" title="' + tumbo.translator.translate(this.config.getOtherProperties()['nameKeyPrevious']) + '"></button>');
            const nextButton = $('<button type="button" class="history-next" title="' + tumbo.translator.translate(this.config.getOtherProperties()['nameKeyNext']) + '"></button>');

            this.uiElement.append(previousButton);
            this.uiElement.append(nextButton);

            previousButton.click(() => {
                this._previous();
                this.logStat('PREVIOUS');
            });
            nextButton.click(() => {
                this._next();
                this.logStat('NEXT');
            });

            this._checkDisabled();
        }
        return this.uiElement;
    }

    _prependHistory(elem) {
        history.unshift(elem);
        // this.logDebug("Added zoomLevel "+elem.zoomLevel+" to history. History now is:",history);
    }
    _cutHistory() {
        // this.logDebug("Will cut history from "+historyIndex+" to "+history.length,history);
        history = history.length > 0 ? history.slice(historyIndex, history.length) : history;
    }

    onNavigation(zoomLevel, centerWgs84, extentWgs84, resolution) {
        if (ignoreLocationHistoryChange) {
            // this.logDebug("Ignored location history change");
            return;
        }

        if(!extentWgs84 || extentWgs84.length!==4)
            return;

        onNavCount++;
        // this.logDebug("Navigation "+onNavCount+". Zoom level: "+zoomLevel);
        if(history.length>0){
            if(onNavCount<=2) {
                // this.logDebug("Ignored count "+onNavCount);
                return;
            }
        }

        extentWgs84 = extentWgs84.map(coord => coord.toFixed(5));

        //check if this is the same location as the last one in history
        let historyEntry = history.length ? history[0] : undefined;
        if (historyEntry &&
            historyEntry.extent[0] === extentWgs84[0] &&
            historyEntry.extent[1] === extentWgs84[1] &&
            historyEntry.extent[2] === extentWgs84[2] &&
            historyEntry.extent[3] === extentWgs84[3]) {
            // this.logDebug("Ignored same extent");
            return;
        }

        this._cutHistory();
        this._prependHistory({center: centerWgs84, zoomLevel: zoomLevel, extent: extentWgs84});
        historyIndex = 0;

        this._checkDisabled();
    }

    _next() {
        if (this._canNext()) {
            historyIndex--;
            this._go();
        }
    }

    _canNext() {
        const canNext = history.length > 1 && historyIndex > 0;
        // this.logDebug("Can next? " + canNext + ", history: " + history.length + ", index: " + historyIndex);
        return canNext;
    }

    _previous() {
        if (this._canPrevious()) {
            historyIndex++;
            this._go();
        }
    }

    _canPrevious() {
        const canPrevious = history.length > 1 && historyIndex <= history.length - 2;
        // this.logDebug("Can previous? " + canPrevious + ", history: " + history.length + ", index: " + historyIndex);
        return canPrevious;
    }

    _go() {
        this._disableBoth();
        let location = history[historyIndex];

        ignoreLocationHistoryChange = true;
        try {
            this.fit(location.extent);
        } catch (e) {
            this.logError("Cannot go to history location. index: " + historyIndex, history);
            this.logError("Error", e);
            this._checkDisabled();
        }
        setTimeout(() => {
            ignoreLocationHistoryChange = false;
            this._checkDisabled();
        }, 1000);
    }

    _disableBoth() {
        $('button.history-previous', this.getUIElement()).addClass('disabled');
        $('button.history-next', this.getUIElement()).addClass('disabled');
    }

    _checkDisabled() {
        const previousButton = $('button.history-previous', this.getUIElement());
        if (this._canPrevious()) {
            previousButton.removeClass('disabled');
        } else {
            previousButton.addClass('disabled');
        }

        const nextButton = $('button.history-next', this.getUIElement());
        if (this._canNext()) {
            nextButton.removeClass('disabled');
        } else {
            nextButton.addClass('disabled');
        }
    }
}

export {CoordinatesTool, LocationHistoryTool, MapPosition}